/**
 * Цвета бренда склада-оператора → токены tokens.css для обеих тем (облако, 28.09).
 *
 * Из одного цвета (primary; accent — если задан, иначе тот же primary) строим
 * набор, который tokens.css задаёт для оранжевого Full-Fix: --brand, --accent,
 * hover/active, мягкие подложки, текст акцентом, кольцо фокуса. Контраст
 * проверяется так же, как в tokens.css: белый текст на кнопке ≥ 4.5:1, текст
 * акцентом на карточке ≥ 4.5:1 (светлая) / ≥ 6:1 (тёмная). Если цвет слишком
 * светлый — затемняем, пока не пройдёт; слишком тёмный для тёмной темы — осветляем.
 */

const clamp = (v, a = 0, b = 255) => Math.min(b, Math.max(a, v));

export function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map((x) => clamp(Math.round(x)).toString(16).padStart(2, '0')).join('').toUpperCase();
}

function luminance([r, g, b]) {
  const c = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Смешать цвет с другим: t=0 — исходный, t=1 — второй */
export function mix(a, b, t) {
  return [0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * t);
}

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];
const DARK_CARD = [0x1e, 0x29, 0x3b];   // --surface-1 тёмной темы

/** Затемнять, пока контраст с фоном не станет ≥ min */
function darkenUntil(c, bg, min) {
  let cur = c;
  for (let i = 0; i < 40 && contrast(cur, bg) < min; i += 1) cur = mix(cur, BLACK, 0.06);
  return cur;
}

/** Осветлять, пока контраст с фоном не станет ≥ min */
function lightenUntil(c, bg, min) {
  let cur = c;
  for (let i = 0; i < 40 && contrast(cur, bg) < min; i += 1) cur = mix(cur, WHITE, 0.06);
  return cur;
}

const rgbStr = (c) => c.map((x) => clamp(Math.round(x))).join(', ');

/**
 * Токены для светлой и тёмной темы.
 * @returns {{light: Object, dark: Object} | null}
 */
export function brandTokens(primaryHex, accentHex) {
  const brand = hexToRgb(primaryHex);
  if (!brand) return null;
  const base = hexToRgb(accentHex) || brand;

  // Кнопка: белый текст ≥ 4.5:1
  const accent = darkenUntil(base, WHITE, 4.6);
  const hover = mix(accent, BLACK, 0.12);
  const active = mix(accent, BLACK, 0.24);
  // Текст акцентом на белом и на мягкой подложке ≥ 4.5:1
  const accentText = darkenUntil(mix(accent, BLACK, 0.1), mix(base, WHITE, 0.92), 5.0);

  const light = {
    '--brand': rgbToHex(brand),
    '--brand-rgb': rgbStr(brand),
    '--accent': rgbToHex(accent),
    '--accent-rgb': rgbStr(accent),
    '--accent-hover': rgbToHex(hover),
    '--accent-active': rgbToHex(active),
    '--accent-soft': rgbToHex(mix(base, WHITE, 0.92)),
    '--accent-soft-hover': rgbToHex(mix(base, WHITE, 0.84)),
    '--accent-text': rgbToHex(accentText),
    '--on-accent': '#FFFFFF',
    '--focus-ring': `0 0 0 3px rgba(${rgbStr(accent)}, 0.28)`,
  };

  // Тёмная тема: заливка кнопки та же (белый текст), подсветки и текст — светлее
  const glow = lightenUntil(base, DARK_CARD, 4.0);
  const darkText = lightenUntil(base, DARK_CARD, 6.5);
  const primaryDark = lightenUntil(base, DARK_CARD, 4.6);   // текст на карточке 4.6:1
  const dark = {
    '--brand': rgbToHex(brand),
    '--brand-rgb': rgbStr(brand),
    '--accent': rgbToHex(accent),
    '--accent-rgb': rgbStr(glow),
    '--accent-hover': rgbToHex(hover),
    '--accent-active': rgbToHex(active),
    '--accent-soft': `rgba(${rgbStr(glow)}, 0.14)`,
    '--accent-soft-hover': `rgba(${rgbStr(glow)}, 0.22)`,
    '--accent-text': rgbToHex(darkText),
    '--on-accent': '#FFFFFF',
    '--focus-ring': `0 0 0 3px rgba(${rgbStr(glow)}, 0.40)`,
    // index.css в тёмной теме задаёт --primary оранжевым Full-Fix числом (~220 мест
    // красят им текст) — у склада облака он должен быть цветом склада (обход 29.09)
    '--primary': rgbToHex(primaryDark),
    '--primary-hover': rgbToHex(lightenUntil(base, DARK_CARD, 5.5)),
    '--primary-rgb': rgbStr(primaryDark),
  };
  return { light, dark };
}

/** CSS-текст, перекрывающий tokens.css (подключается <style> после него) */
export function brandCss(primaryHex, accentHex) {
  const t = brandTokens(primaryHex, accentHex);
  if (!t) return '';
  const block = (obj) => Object.entries(obj).map(([k, v]) => `  ${k}: ${v};`).join('\n');
  return `:root,\n[data-theme="light"] {\n${block(t.light)}\n}\n[data-theme="dark"] {\n${block(t.dark)}\n}\n`;
}
